import { defineStore, storeToRefs } from "pinia"
import { ref } from "vue"
import { eventbus } from "@/helpers/eventbus"
import { sleep } from "@/helpers/util"
import { useApiStore } from "@/stores/api"
import { useDatabaseStore } from "@/stores/database"

export const useSyncStore = defineStore(
	"sync",
	() => {
		const debug = true

		const api = useApiStore()
		const database = useDatabaseStore()
		const { connectionProblemOccured } = storeToRefs(useApiStore())

		const syncInProgress = ref(false)
		const syncPercentage = ref(0)
		const syncCheckInProgress = ref(false)
		const syncCurrentJob = ref(null)
		const syncOffset = ref(null)
		const syncedRecords = ref(null)
		const initialized = ref(false)
		const initialSyncDone = ref(false)

		/* init */

		const init = async () => {
			if (debug) console.log("🔄 init()")
			await database.waitForInitialisation()
			await updateSyncOffset()
			initialized.value = true
			syncInProgress.value = false
			syncCheckInProgress.value = false
			if (syncedRecords.value > 0) {
				initialSyncDone.value = true
			}
			if (debug) console.log("🔄 init() done.")
		}

		const reset = async () => {
			syncInProgress.value = false
			syncPercentage.value = 0
			syncCheckInProgress.value = false
			syncCurrentJob.value = null
			syncOffset.value = null
			syncedRecords.value = null
			initialSyncDone.value = false

			await database.reset()
			await updateSyncOffset()

			eventbus.emit("routeUpdated")
		}

		const waitForInitialisation = async () => {
			while (!initialized.value) {
				await sleep(50)
			}
		}

		/* chunk sync */
		const sync = async () => {
			await waitForInitialisation()

			if (debug) console.log("🔄 sync called.")
			if (syncInProgress.value) {
				if (debug) console.log("🔄 sync in progress, cancelling.")
				return false
			}
			if (debug) console.log(`🔄 staring sync (offset: ${syncOffset.value})`)

			const table = "positionlogs"
			const columns = ["id", "tracker_id", "fix_at", "fix_timezone", "latitude", "longitude", "altitude", "speed", "course", "distance", "rssi", "sat", "battery", "voltage"]
			const columnsNumber = columns.length

			syncInProgress.value = true
			syncPercentage.value = 0
			syncCurrentJob.value = "connecting"
			let firstChunk = true
			let totalRecords = 1000 // first estimation
			let syncedRecords = 0
			let chunkRecords = 0

			do {
				const isFirstChunkOfInitialSync = !initialSyncDone.value && firstChunk

				let response = null
				syncCurrentJob.value = "loading"
				try {
					response = await api.dispatchApiCall(
						"post",
						`/api/v2/trackers/sync`,
						{
							// batch_size: 20_000,
							...(isFirstChunkOfInitialSync && { initial_sync: true }),
							...(syncOffset.value && { offset: syncOffset.value }),
						},
						{
							//Accept: "text/csv",
						},
						60000
					)
				} catch (e) {
					connectionProblemOccured.value = true
					syncInProgress.value = false
					syncCurrentJob.value = null
					initialSyncDone.value = true
					return
				}

				if (isFirstChunkOfInitialSync) {
					totalRecords = response.headers.get("trackiwi-position-count")
				}

				const data = response.data ? response.data.trim().split(/\r?\n/)
					.reduce((acc, line) => {
						const cols = line.split(",")
						if (cols.length === columnsNumber) acc.push(cols)
						return acc
					}, []) : []

				chunkRecords = data.length

				if (chunkRecords > 0) {
					syncCurrentJob.value = "importing"
					await database.batchInsert(table, columns, data)
					await updateSyncOffset()
				}
				syncedRecords += chunkRecords
				syncPercentage.value = Math.min(100, Math.round((syncedRecords / totalRecords) * 100))
				firstChunk = false
			} while (chunkRecords > 0)

			if (syncedRecords) {
				await database.persist()
				eventbus.emit("routeUpdated")
			}

			syncInProgress.value = false
			syncCurrentJob.value = null
			initialSyncDone.value = true

			if (debug) console.log("🔄 sync done")
			return true
		}

		const updateSyncOffset = async () => {
			try {
				const result = await database.query(`SELECT MAX(id) as max, count(*) as count FROM positionlogs;`)
				syncOffset.value = result.values[0].max
				syncedRecords.value = result.values[0].count
			} catch (e) {
				console.error(e)
			}
		}

		const resync = async () => {
			await reset()
			await sleep(2000)
			await sync()
		}

		init()

		return {
			reset,
			sync,
			resync,
			initialized,
			syncInProgress,
			syncPercentage,
			syncCurrentJob,
			initialSyncDone,
			syncedRecords,
			syncOffset,
		}
	},
	{
		persist: false,
	}
)
